import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { NextLaunchContext, UserContext, TenantBranding } from '@/types/api'
import { authApiService } from '@/services/authApiService'
import { logger, createUserLogger } from '@/services/logger'

// Session expires after 8 hours of inactivity
const SESSION_EXPIRY_MS = 8 * 60 * 60 * 1000

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserContext | null>(null)
  const tenantBranding = ref<TenantBranding | null>(null)
  const launchContext = ref<NextLaunchContext | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isSessionExpired = (): boolean => {
    const storedTimestamp = localStorage.getItem('authTimestamp')
    if (!storedTimestamp) return true
    
    const timestamp = parseInt(storedTimestamp, 10)
    const now = Date.now()
    const expired = (now - timestamp) > SESSION_EXPIRY_MS
    
    if (expired) {
      logger.info('Session expired', { 
        storedAt: new Date(timestamp).toISOString(),
        expiredAfterHours: ((now - timestamp) / (60 * 60 * 1000)).toFixed(1)
      })
    }
    
    return expired
  }

  const clearStoredAuth = () => {
    localStorage.removeItem('currentUserGuid')
    localStorage.removeItem('currentUser')
    localStorage.removeItem('authTimestamp')
  }

  const initializeFromStorage = async () => {
    const storedLaunchContext = sessionStorage.getItem('nextLaunchContext')
    const storedUserGuid = localStorage.getItem('currentUserGuid')
    const storedUser = localStorage.getItem('currentUser')

    if (storedLaunchContext) {
      try {
        applyNextLaunchContext(JSON.parse(storedLaunchContext) as NextLaunchContext)
      } catch (err) {
        logger.warn('Failed to restore Next launch context', { error: err })
        sessionStorage.removeItem('nextLaunchContext')
      }
    }

    if (storedUserGuid && storedUser) {
      // Check if session has expired
      if (isSessionExpired()) {
        logger.info('Clearing expired session', { userGuid: storedUserGuid })
        clearStoredAuth()
        return
      }

      try {
        // Immediately restore cached user for fast render
        user.value = JSON.parse(storedUser)
        isAuthenticated.value = true

        // Update timestamp on successful restore (extends session)
        localStorage.setItem('authTimestamp', Date.now().toString())

        logger.info('Restored authentication from localStorage', {
          userGuid: storedUserGuid,
          tenantId: user.value?.tenantId
        })

        // Load tenant branding (uses cached user, no re-auth needed)
        // The IsLocalRequest check on /api/auth/authenticate blocks browser calls,
        // so we skip background re-auth and trust the cached user context.
        if (user.value?.tenantId) {
          try {
            await loadTenantBranding(user.value.tenantId)
          } catch (brandingError) {
            logger.warn('Failed to load tenant branding on restore', { error: brandingError })
          }
        }
      } catch (err) {
        logger.error('Failed to restore authentication from localStorage', { error: err })
        clearStoredAuth()
      }
    }
  }

  const currentUser = computed(() => user.value)
  const currentTenant = computed(() => user.value?.tenantId)
  const userPermissions = computed(() => user.value?.permissions || [])
  const userRoles = computed(() => user.value?.roles || [])

  function applyNextLaunchContext(context: NextLaunchContext | null) {
    launchContext.value = context

    if (!context) {
      sessionStorage.removeItem('nextLaunchContext')
      return
    }

    sessionStorage.setItem('nextLaunchContext', JSON.stringify(context))

    tenantBranding.value = {
      primaryColor: tenantBranding.value?.primaryColor || '#2F6FA3',
      secondaryColor: tenantBranding.value?.secondaryColor || '#F26A2E',
      logoUrl: tenantBranding.value?.logoUrl || '/logo.png',
      companyName: context.tenantName || tenantBranding.value?.companyName || 'Incentit IQ',
      sourceApp: context.sourceApp,
      returnUrl: context.returnUrl
    }

    logger.info('Applied Next launch context', {
      tenantName: context.tenantName,
      themeName: context.themeName,
      returnUrl: context.returnUrl,
      hasHandoffToken: Boolean(context.handoffToken)
    })
  }

  async function authenticateUser(userGuid: string, options?: { silent?: boolean }) {
    const silent = options?.silent ?? false
    if (!silent) {
      isLoading.value = true
      error.value = null
    }

    try {
      logger.info('Starting authentication', { userGuid, silent })
      localStorage.setItem('currentUserGuid', userGuid)

      const response = await authApiService.authenticateUser(userGuid)
      setAuthenticatedUser(response.data, userGuid)

      const userLogger = createUserLogger(userGuid)
      userLogger.info('User authenticated successfully', {
        tenantId: response.data.tenantId,
        tenantName: response.data.tenantName
      })

      try {
        await loadTenantBranding(response.data.tenantId)
      } catch (brandingError) {
        logger.warn('Failed to load tenant branding, using defaults', {
          tenantId: response.data.tenantId,
          error: brandingError
        })
      }

      logger.info('Authentication completed', {
        userId: response.data.userId,
        tenantId: response.data.tenantId
      })
    } catch (err) {
      if (!silent) {
        error.value = err instanceof Error ? err.message : 'Authentication failed'
      }
      logger.error('Authentication failed', { userGuid, error: err, silent })
      throw err
    } finally {
      if (!silent) {
        isLoading.value = false
      }
    }
  }

  async function authenticateWithHandoff(handoffToken: string) {
    isLoading.value = true
    error.value = null

    try {
      logger.info('Starting Next handoff authentication')
      const response = await authApiService.authenticateWithHandoff(handoffToken)
      const resolvedUserGuid = response.data.applicationUserId || response.data.userId

      if (!resolvedUserGuid) {
        throw new Error('Handoff response did not include an application user id')
      }

      setAuthenticatedUser(response.data, resolvedUserGuid)

      try {
        await loadTenantBranding(response.data.tenantId)
      } catch (brandingError) {
        logger.warn('Failed to load tenant branding after handoff', {
          tenantId: response.data.tenantId,
          error: brandingError
        })
      }

      logger.info('Next handoff authentication completed', {
        userId: response.data.userId,
        tenantId: response.data.tenantId
      })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Handoff authentication failed'
      logger.error('Next handoff authentication failed', { error: error.value })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function loadTenantBranding(tenantId: string) {
    // The /api/tenants/{id}/branding endpoint doesn't exist yet.
    // Use defaults based on launch context until tenant branding is implemented.
    tenantBranding.value = {
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoUrl: '/logo.png',
      companyName: launchContext.value?.tenantName || 'Incentit IQ',
      sourceApp: launchContext.value?.sourceApp,
      returnUrl: launchContext.value?.returnUrl
    }
    logger.debug('Using default tenant branding', { tenantId })
  }

  function setAuthenticatedUser(authenticatedUser: UserContext, userGuid: string) {
    user.value = authenticatedUser
    isAuthenticated.value = true
    localStorage.setItem('currentUserGuid', userGuid)
    localStorage.setItem('currentUser', JSON.stringify(authenticatedUser))
    localStorage.setItem('authTimestamp', Date.now().toString())
    sessionStorage.setItem('currentUserGuid', userGuid)

    // Set user on Grafana Faro for session attribution
    try {
      const faro = (window as any).GrafanaFaroWebSdk?.faro ?? (window as any).__FARO_INSTANCE__
      if (faro?.api?.setUser) {
        faro.api.setUser({
          id: userGuid,
          username: authenticatedUser.userName || authenticatedUser.email || userGuid,
          email: authenticatedUser.email || undefined,
          attributes: { tenant: authenticatedUser.tenantName || '' }
        })
      }
    } catch { /* Faro not loaded yet — non-critical */ }
  }

  function logout() {
    user.value = null
    tenantBranding.value = null
    launchContext.value = null
    isAuthenticated.value = false
    error.value = null

    localStorage.removeItem('currentUserGuid')
    localStorage.removeItem('currentUser')
    localStorage.removeItem('authTimestamp')
    sessionStorage.removeItem('currentUserGuid')
    sessionStorage.removeItem('nextLaunchContext')

    logger.info('User logged out')
  }

  function hasPermission(permission: string): boolean {
    return userPermissions.value.includes(permission)
  }

  function hasRole(role: string): boolean {
    return userRoles.value.includes(role)
  }

  function clearError() {
    error.value = null
  }

  function updateUser(newUserContext: any) {
    user.value = newUserContext
    localStorage.setItem('currentUser', JSON.stringify(newUserContext))
  }

  return {
    user,
    currentUser,
    tenantBranding,
    launchContext,
    isAuthenticated,
    isLoading,
    error,
    currentTenant,
    userPermissions,
    userRoles,
    initializeFromStorage,
    applyNextLaunchContext,
    authenticateUser,
    authenticateWithHandoff,
    loadTenantBranding,
    logout,
    hasPermission,
    hasRole,
    clearError,
    updateUser
  }
})
